import { useEffect, useMemo, useState } from 'react';
import { isRtlOverhaulEnabled } from '../utils/featureFlags';

// Languages that use RTL direction
const RTL_LANGUAGES = new Set(['ar', 'fa', 'ur', 'he']);

export interface UnifiedDirectionOptions {
  language?: string;
  forceDirection?: 'ltr' | 'rtl';
  enableFeatureFlag?: boolean;
}

export interface UnifiedDirectionResult {
  dir: 'ltr' | 'rtl';
  isRTL: boolean;
  language: string;
  isOverhaulEnabled: boolean;
}

/**
 * Hook موحد لإدارة الاتجاهات في التطبيق
 * Unified hook for managing text direction across the app
 */
export function useUnifiedDirection(options: UnifiedDirectionOptions = {}): UnifiedDirectionResult {
  const { 
    language: propLanguage, 
    forceDirection, 
    enableFeatureFlag = true 
  } = options;

  const [browserLanguage, setBrowserLanguage] = useState<string>('ar');
  const isOverhaulEnabled = enableFeatureFlag ? isRtlOverhaulEnabled() : true;

  // Get browser language on mount
  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      const navLang = (navigator.language || navigator.languages?.[0] || 'ar')
        .split('-')[0]
        .toLowerCase();
      setBrowserLanguage(navLang);
    }
  }, []);

  // Get stored language from localStorage
  const storedLanguage = useMemo(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'ar';
    }
    return 'ar';
  }, []);

  // Determine final language
  const finalLanguage = propLanguage || storedLanguage || browserLanguage || 'ar';

  // Determine direction
  const dir = useMemo<'ltr' | 'rtl'>(() => {
    // If direction is forced, use it
    if (forceDirection) return forceDirection;
    
    // If feature flag is disabled, use LTR for non-admin areas
    if (!isOverhaulEnabled && !propLanguage) return 'ltr';
    
    // Check if language is RTL
    return RTL_LANGUAGES.has(finalLanguage.toLowerCase()) ? 'rtl' : 'ltr';
  }, [finalLanguage, forceDirection, isOverhaulEnabled, propLanguage]);

  // Update document direction when it changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.setAttribute('dir', dir);
      root.setAttribute('lang', finalLanguage);
      
      // Add/remove RTL class for CSS targeting
      if (dir === 'rtl') {
        root.classList.add('rtl');
        root.classList.remove('ltr');
      } else {
        root.classList.add('ltr');
        root.classList.remove('rtl');
      }
    }
  }, [dir, finalLanguage]);

  return {
    dir,
    isRTL: dir === 'rtl',
    language: finalLanguage,
    isOverhaulEnabled
  };
}

/**
 * Hook مبسط للحصول على الاتجاه فقط
 * Simplified hook to get direction only
 */
export function useDirection(language?: string): 'ltr' | 'rtl' {
  const { dir } = useUnifiedDirection({ language });
  return dir;
}

/**
 * Hook للحصول على فئات CSS للاتجاه
 * Hook to get CSS classes for direction
 */
export function useDirectionClasses(language?: string) {
  const { dir, isRTL } = useUnifiedDirection({ language });
  
  return {
    dir,
    isRTL,
    flexDirection: isRTL ? 'flex-row-reverse' : 'flex-row',
    textAlign: isRTL ? 'text-right' : 'text-left',
    float: isRTL ? 'float-right' : 'float-left',
    marginStart: isRTL ? 'mr' : 'ml',
    marginEnd: isRTL ? 'ml' : 'mr',
    paddingStart: isRTL ? 'pr' : 'pl',
    paddingEnd: isRTL ? 'pl' : 'pr',
    borderStart: isRTL ? 'border-r' : 'border-l',
    borderEnd: isRTL ? 'border-l' : 'border-r',
    roundedStart: isRTL ? 'rounded-r' : 'rounded-l',
    roundedEnd: isRTL ? 'rounded-l' : 'rounded-r'
  };
}